/**
 * SystemLayout
 *
 * Top-level shell. Composes the Sidebar, AppHeader, route Outlet, and
 * cross-cutting modals (changelog). Heavy chrome logic lives in the child
 * components – this file should remain a coordinator.
 *
 * Navigation is adaptive: desktop/tablet keep the collapsible sidebar, while
 * phones get a bottom nav bar (see BottomNav) instead of a hamburger drawer.
 */

import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Layout, message, Spin } from 'antd';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import Sidebar from './Sidebar';
import AppHeader from './AppHeader';
import BottomNav from './BottomNav';
import ChangelogModal from './ChangelogModal';
import { useResponsive } from '../../core/hooks/useResponsive';
import { getStudentHomeworkNotification } from '../../core/api/services/studentService';
import { getTeacherPracticeTestNotifications } from '../../services/api-client';
import './layout.css';

const { Content } = Layout;

const useNotifications = (role) => {
  const [items, setItems] = useState([]);

  const refetch = useCallback(async () => {
    try {
      if (role === 'STUDENT') {
        const res = await getStudentHomeworkNotification();
        const { success, data, message: errorMessage } = res.data || {};
        if (success) {
          setItems(data || []);
        } else if (errorMessage) {
          message.error(errorMessage);
        }
      } else if (role === 'TEACHER') {
        const res = await getTeacherPracticeTestNotifications();
        const { success, data } = res.data || {};
        if (success) setItems(data || []);
      } else {
        setItems([]);
      }
    } catch (err) {
      console.warn('Notifications fetch failed', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { items, refetch };
};

/**
 * Shown while a lazily-loaded route chunk resolves. Rendered inside the shell
 * so the header and bottom nav stay put – only the content area swaps.
 */
const ContentFallback = () => (
  <div className="lms-route-fallback">
    <Spin size="large" />
  </div>
);

const SystemLayout = ({ user, isConnected }) => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useResponsive();
  const [collapsed, setCollapsed] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);

  const { items: notifications, refetch: refetchNotifications } = useNotifications(user?.roleName);

  // On tablet, collapse by default.
  useEffect(() => {
    if (isTablet) setCollapsed(true);
  }, [isTablet]);

  const onToggleSidebar = useCallback(() => {
    setCollapsed((v) => !v);
  }, []);

  const contentShellClass = useMemo(() => {
    if (isMobile) return 'lms-content-shell lms-content-shell--mobile';
    return collapsed
      ? 'lms-content-shell lms-content-shell--desktop-collapsed'
      : 'lms-content-shell lms-content-shell--desktop';
  }, [isMobile, collapsed]);

  const outletContext = useMemo(() => ({ refetchNotifications }), [refetchNotifications]);

  return (
    <Layout className="lms-app-layout">
      {/* First focusable element: a keyboard user reaches the page without
          tabbing through the whole sidebar first (app-shell.md § Accessibility). */}
      <a href="#lms-main" className="lms-skip-link">
        {t('common.skipToContent')}
      </a>
      {!isMobile && (
        <Sidebar
          user={user}
          collapsed={collapsed}
          onOpenChangelog={() => setChangelogOpen(true)}
        />
      )}

      <div className={contentShellClass}>
        <AppHeader
          isMobile={isMobile}
          collapsed={collapsed}
          onToggleSidebar={onToggleSidebar}
          isConnected={isConnected}
          notificationCount={notifications.length}
          homeworkNotifications={notifications}
          user={user}
        />

        <Content className="lms-content" id="lms-main">
          <Suspense fallback={<ContentFallback />}>
            <Outlet context={outletContext} />
          </Suspense>
        </Content>
      </div>

      {isMobile && (
        <BottomNav user={user} onOpenChangelog={() => setChangelogOpen(true)} />
      )}

      <ChangelogModal
        open={changelogOpen}
        onClose={() => setChangelogOpen(false)}
        isMobile={isMobile}
      />
    </Layout>
  );
};

SystemLayout.propTypes = {
  user: PropTypes.object,
  isConnected: PropTypes.bool,
};

export default SystemLayout;
